import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getStoneRequirement, listStoneQuotes, submitStoneQuote, updateStoneQuote,
  uploadImage, getImageUrl, formatApiError,
} from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { STONE_KINDS, CERTIFICATE_TYPES_QUOTE } from '@/utils/constants';
import { ArrowLeft, Save, Upload, X, Loader2 } from 'lucide-react';

const KIND = Object.fromEntries(STONE_KINDS.map(k => [k.value, k.label]));

const BLANK = {
  price_per_ct: '',
  price_total: '',
  currency: 'INR',
  ct_per_stone_offered: '',
  qty_offered: '',
  total_ctw_offered: '',
  stone_images: [],
  certificate_image: '',
  certificate_type: 'none',
  certificate_number: '',
  notes: '',
};

export default function SupplierQuoteFormPage() {
  const { id: requirementId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([
      getStoneRequirement(requirementId),
      listStoneQuotes(requirementId),
    ]).then(([r, q]) => {
      setReq(r.data.data);
      const mine = (q.data.data || []).find(x => x.supplier_id === user?.id);
      if (mine) {
        setExisting(mine);
        setForm({
          price_per_ct: mine.price_per_ct ?? '',
          price_total: mine.price_total ?? '',
          currency: mine.currency || 'INR',
          ct_per_stone_offered: mine.ct_per_stone_offered ?? '',
          qty_offered: mine.qty_offered ?? '',
          total_ctw_offered: mine.total_ctw_offered ?? '',
          stone_images: mine.stone_images || [],
          certificate_image: mine.certificate_image || '',
          certificate_type: mine.certificate_type || 'none',
          certificate_number: mine.certificate_number || '',
          notes: mine.notes || '',
        });
      } else if (r.data.data) {
        // Pre-fill offered specs from requirement
        const rd = r.data.data;
        setForm(f => ({
          ...f,
          ct_per_stone_offered: rd.ct_per_stone ?? '',
          qty_offered: rd.qty ?? '',
          total_ctw_offered: rd.total_ctw ?? '',
        }));
      }
    }).catch(() => { toast.error('Failed to load'); navigate('/stones'); })
      .finally(() => setLoading(false));
  }, [requirementId, user?.id, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-compute price_total when price_per_ct + total_ctw_offered both set and total empty
  useEffect(() => {
    const ppc = parseFloat(form.price_per_ct), totalCt = parseFloat(form.total_ctw_offered);
    if (ppc && totalCt && !form.price_total) set('price_total', (ppc * totalCt).toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.price_per_ct, form.total_ctw_offered]);

  const handleUpload = async (e, field) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const res = await uploadImage(f);
        // Backend /api/upload returns { image: "/api/uploads/<filename>" }
        const url = res.data?.image || res.data?.path || res.data?.url;
        if (url) paths.push(url);
      }
      if (!paths.length) throw new Error('Upload returned no usable path');
      if (field === 'certificate_image') set('certificate_image', paths[0]);
      else set('stone_images', [...form.stone_images, ...paths]);
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!form.price_per_ct && !form.price_total) {
      toast.error('Enter price per ct OR total price'); return;
    }
    setSaving(true);
    try {
      const payload = {
        requirement_id: requirementId,
        price_per_ct: form.price_per_ct ? parseFloat(form.price_per_ct) : null,
        price_total: form.price_total ? parseFloat(form.price_total) : null,
        currency: form.currency || 'INR',
        ct_per_stone_offered: form.ct_per_stone_offered ? parseFloat(form.ct_per_stone_offered) : null,
        qty_offered: form.qty_offered ? parseInt(form.qty_offered) : null,
        total_ctw_offered: form.total_ctw_offered ? parseFloat(form.total_ctw_offered) : null,
        stone_images: form.stone_images,
        certificate_image: form.certificate_image,
        certificate_type: form.certificate_type || 'none',
        certificate_number: form.certificate_number || '',
        notes: form.notes,
      };
      if (existing) {
        const { requirement_id, ...patch } = payload;
        await updateStoneQuote(existing.id, patch);
        toast.success('Quote updated');
      } else {
        await submitStoneQuote(payload);
        toast.success('Quote submitted');
      }
      navigate(`/stones/${requirementId}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed'));
    } finally { setSaving(false); }
  };

  if (loading) return <CollapsibleLayout><p className="text-sm text-center py-12">Loading…</p></CollapsibleLayout>;
  if (!req) return null;

  return (
    <CollapsibleLayout>
      <StoneTabs active="requirements" />
      <div className="max-w-[900px] mx-auto p-4 sm:p-6 space-y-5">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/stones/${requirementId}`)}><ArrowLeft size={14} /></Button>
            <div>
              <h1 className="text-lg font-semibold">{existing ? 'Edit' : 'Submit'} Quote — <span className="font-mono">{req.requirement_no}</span></h1>
              <p className="text-xs text-muted-foreground">
                {req.gemstone_type} · {KIND[req.stone_kind] || req.stone_kind} · {req.size_mm || 'size n/a'}
                {req.qty ? ` · Qty ${req.qty}` : ''}
                {req.total_ctw ? ` · ${req.total_ctw} ct total` : ''}
              </p>
            </div>
          </div>
          <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-quote">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            {existing ? 'Update Quote' : 'Submit Quote'}
          </Button>
        </header>

        <section className="bg-white rounded-lg border p-4 sm:p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">PRICE</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px]">Currency</Label>
              <Input value={form.currency} onChange={e => set('currency', e.target.value.toUpperCase())} className="h-10 text-base mt-0.5" placeholder="INR" />
            </div>
            <div>
              <Label className="text-[10px]">Price / ct</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={form.price_per_ct} onChange={e => set('price_per_ct', e.target.value)} className="h-10 text-base mt-0.5" data-testid="price-per-ct" />
            </div>
            <div>
              <Label className="text-[10px]">Total Price</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={form.price_total} onChange={e => set('price_total', e.target.value)} className="h-10 text-base mt-0.5" data-testid="price-total" />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border p-4 sm:p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">OFFERED SPECS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px]">Ct / stone</Label>
              <Input type="number" inputMode="decimal" step="0.001" value={form.ct_per_stone_offered} onChange={e => set('ct_per_stone_offered', e.target.value)} className="h-10 text-base mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px]">Qty</Label>
              <Input type="number" inputMode="numeric" value={form.qty_offered} onChange={e => set('qty_offered', e.target.value)} className="h-10 text-base mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px]">Total Ct</Label>
              <Input type="number" inputMode="decimal" step="0.001" value={form.total_ctw_offered} onChange={e => set('total_ctw_offered', e.target.value)} className="h-10 text-base mt-0.5" />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border p-5 space-y-3">
          <h2 className="text-xs font-semibold text-muted-foreground">STONE IMAGES</h2>
          <div className="flex gap-3 flex-wrap items-start">
            {form.stone_images.map((p, i) => (
              <div key={i} className="relative">
                <img src={getImageUrl(p)} alt="" className="w-24 h-24 object-cover rounded border" />
                <button onClick={() => set('stone_images', form.stone_images.filter(x => x !== p))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  <X size={10} />
                </button>
              </div>
            ))}
            <label className="w-24 h-24 border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 text-xs text-muted-foreground" title="Click to add — you can select multiple images at once">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={16} />}
              <span className="mt-1 text-center leading-tight">Add<br/><span className="text-[9px]">(multiple OK)</span></span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleUpload(e, 'stone_images')} disabled={uploading} />
            </label>
          </div>
          <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px]">Certificate type</Label>
              <Select value={form.certificate_type || 'none'} onValueChange={v => set('certificate_type', v)}>
                <SelectTrigger className="h-10 text-base mt-0.5" data-testid="quote-cert-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CERTIFICATE_TYPES_QUOTE.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.certificate_type && form.certificate_type !== 'none' && (
              <div>
                <Label className="text-[10px]">Certificate number</Label>
                <Input value={form.certificate_number} onChange={e => set('certificate_number', e.target.value)} className="h-10 text-base mt-0.5" placeholder="e.g. 1234567890" />
              </div>
            )}
          </div>
          <div className="pt-2">
            <Label className="text-[10px]">Certificate image (optional)</Label>
            <div className="flex gap-2 items-start mt-1">
              {form.certificate_image ? (
                <div className="relative">
                  <img src={getImageUrl(form.certificate_image)} alt="cert" className="w-24 h-24 object-cover rounded border-2 border-amber-300" />
                  <button onClick={() => set('certificate_image', '')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <label className="w-24 h-24 border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 text-xs text-muted-foreground">
                  <Upload size={16} />
                  <span className="mt-1">Cert</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, 'certificate_image')} disabled={uploading} />
                </label>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border p-5 space-y-2">
          <Label className="text-[10px]">Notes</Label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            className="w-full text-xs p-2 border rounded-md bg-slate-50 min-h-[80px] resize-y"
            placeholder="Quality grading, origin, availability, etc." />
        </section>
      </div>
    </CollapsibleLayout>
  );
}
